import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import Tour from 'reactour';

import { SubsetForm } from '../components/subset-form';
import { useGetDocIdsByQueryKey } from '@/api/queries/useGetDocIdsByQueryKey';
import { useGetDocIds } from '@/api/queries/useGetDocs';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSuggestion } from '@/api/queries/useGetSuggestion';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { SubsetCreationOnboarding } from '@/new/components/onboarding/subset-creation-onboarding';
import { SubsetCreationV2Onboarding } from '@/new/components/onboarding/subset-creation-v2-onboarding';
import { SUBSET_CREATION_TOUR_STEPS } from '@/new/constants/tour-steps';
import { useTour } from '@/new/hooks/useTour';

export function SubsetCreator() {
  const { matterId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const suggestionId = searchParams.get('suggestionId');
  const documentIdsParam = searchParams.get('documentIds');
  const folderId = searchParams.get('folderId');
  const factsQueryKey = searchParams.get('factsQueryKey');

  const { isOpen: isTourOpen, openTour, closeTour } = useTour('subset-creation');

  const fromChecklistSubsetCreation = Boolean(location?.state?.fromChecklistSubsetCreation);

  if (!matterId) return null;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: suggestion, isLoading: isLoadingSuggestion } = useGetSuggestion(suggestionId);

  const { data: folderDocIdsData, isLoading: isLoadingFolderDocs } = useGetDocIds({
    matterId,
    subsetId: matter?.allDocumentsCaseId ?? '',
    filters: { folderId: folderId ?? undefined },
    isFlattened: true,
    enabled: !!folderId && !!matter?.allDocumentsCaseId,
  });

  const { data: factsDocIdsData, isLoading: isLoadingFactsDocs } = useGetDocIdsByQueryKey(matterId, factsQueryKey);

  // Prepare initial data from suggestion, documentIds param, folderId, or factsQueryKey
  const initialData = suggestion?.docIds
    ? {
        name: suggestion.name,
        issues: [],
        selectedDocIds: suggestion.docIds,
      }
    : documentIdsParam
      ? {
          name: '',
          issues: [],
          selectedDocIds: documentIdsParam.split(',').filter(Boolean),
        }
      : folderId && folderDocIdsData?.docIds
        ? {
            name: '',
            issues: [],
            selectedDocIds: folderDocIdsData.docIds,
          }
        : factsQueryKey && factsDocIdsData?.docIds
          ? {
              name: '',
              issues: [],
              selectedDocIds: factsDocIdsData.docIds,
            }
          : undefined;

  const isLoadingInitial =
    (!!suggestionId && (isLoadingSuggestion || !suggestion)) ||
    (!!folderId && isLoadingFolderDocs) ||
    (!!factsQueryKey && isLoadingFactsDocs);

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                { href: `/matters/${matterId}`, label: matter.name, active: false, iconClass: 'lucide--folder' },
                { href: `/matters/${matterId}/subsets`, label: 'Subsets', active: false },
                { href: `/matters/${matterId}/subsets/new`, label: 'New', active: true },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <SubsetForm
        key={suggestion?.id || (suggestionId && 'loading') || documentIdsParam || folderId || factsQueryKey || 'create'}
        mode="create"
        matterId={matterId}
        initialData={initialData}
        suggestionId={suggestionId || undefined}
        isLoadingInitialData={isLoadingInitial}
        showIncludedByDefault={!!folderId || !!suggestionId || !!factsQueryKey || !!documentIdsParam}
      />

      {fromChecklistSubsetCreation && <SubsetCreationOnboarding />}
      <SubsetCreationV2Onboarding onTakeTour={openTour} />

      {/* @ts-ignore: Tour unsupported types */}
      <Tour startAt={0} steps={SUBSET_CREATION_TOUR_STEPS} isOpen={isTourOpen} onRequestClose={closeTour} />
    </div>
  );
}
